import { RefObject, useCallback, useRef, useState } from "react";
import useSpaces from "./useSpaces";
import mapboxgl from "mapbox-gl";

const useBarriers = (map: RefObject<mapboxgl.Map | null>) => {
	const drawingState = useRef<{
		isDrawing: boolean;
		points: [number, number][];
		markers: mapboxgl.Marker[];
	}>({ isDrawing: false, points: [], markers: [] });

	const [barrierMarkers, setBarrierMarkers] = useState<{
		[key: string]: mapboxgl.Marker[];
	}>({});

	const { barrierSpaces, activeSpaces, toggleSpaceActive } = useSpaces();

	const handleBarrierMarkers = useCallback(
		(spaceType: string) => {
			// Get the space configuration
			const spaceConfig =
				barrierSpaces[spaceType as keyof typeof barrierSpaces];
			console.log(spaceConfig);
			const barrierColor = spaceConfig.color || "#FF0000";

			const clickHandler = (e: mapboxgl.MapMouseEvent) => {
				const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat];

				// Add point to drawing state
				drawingState.current.points.push(coords);

				// Add visual marker
				const marker = new mapboxgl.Marker({
					color: barrierColor,
					scale: 0.5,
				})
					.setLngLat(coords)
					.addTo(map.current!);

				drawingState.current.markers.push(marker);

				// If we have 4 points, create the polygon
				if (drawingState.current.points.length === 4) {
					const polygonId = `barrier-${Date.now()}`;
					const coordinates = [
						...drawingState.current.points,
						drawingState.current.points[0],
					];

					// Add the polygon to the map
					map.current?.addSource(polygonId, {
						type: "geojson",
						data: {
							type: "Feature",
							geometry: {
								type: "Polygon",
								coordinates: [coordinates],
							},
							properties: {
								spaceType: activeSpaces.barriers,
								color: barrierColor,
							},
						},
					});

					map.current?.addLayer({
						id: `${polygonId}-fill`,
						type: "fill",
						source: polygonId,
						paint: {
							"fill-color": barrierColor,
							"fill-opacity": 0.3,
						},
					});

					map.current?.addLayer({
						id: `${polygonId}-line`,
						type: "line",
						source: polygonId,
						paint: {
							"line-color": barrierColor,
							"line-width": 2,
						},
					});

					// Save to state
					setBarrierMarkers((prev: any) => ({
						...prev,
						[activeSpaces.barriers as string]: [
							...(prev[activeSpaces.barriers as string] || []),
							{
								id: polygonId,
								coordinates: coordinates,
								type: "polygon",
							},
						],
					}));

					// Clean up
					drawingState.current.markers.forEach((m) => m.remove());
					drawingState.current.points = [];
					drawingState.current.markers = [];

					// Reset active space
					toggleSpaceActive("", "barriers");

					// Remove click handler
					map.current?.off("click", clickHandler);
				}
			};

			// Remove any existing click handler
			map.current?.off("click", clickHandler);

			// Reset drawing state
			drawingState.current = {
				isDrawing: true,
				points: [],
				markers: [],
			};

			// Add new click handler
			map.current?.on("click", clickHandler);

			return () => {
				if (map.current) {
					map.current?.off("click", clickHandler);
					drawingState.current.markers.forEach((m) => m.remove());
				}
			};
		},
		[map, activeSpaces, barrierSpaces, setBarrierMarkers, toggleSpaceActive]
	);

	return { barrierMarkers, setBarrierMarkers, handleBarrierMarkers };
};

export default useBarriers;
